import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';
import { TooltipModule } from 'primeng/tooltip';
import { CesIconComponent } from '../ces-icon/ces-icon.component';
import { CesFastDateRangeInputComponent } from '../inputs/ces-fast-date-range-input.component';
import { CesTooltipAutoDismissDirective } from '../../../directives/ces-tooltip-auto-dismiss.directive';
import {
  buildDateRangeTooltip,
  DateRangeTuple,
  dateInRange,
  dateRangeEquals,
  dateRangeIsActive,
  normalizeDateRange,
} from '../../../utils/date-range-filter.utils';

/**
 * AG-Grid column filter over a `[from, to]` day range, driven by the same
 * ces-fast-date-range-input as the PrimeNG grids so every date filter on the
 * site shares one behavior: compact `dd.mm.yyyy[-dd.mm.yyyy]` syntax, commit
 * on Enter, local-day semantics. The model is the DateRangeTuple itself; grids
 * either evaluate it client-side (doesFilterPass) or read it from the filter
 * model to build server-side requests.
 */
@Component({
  selector: 'ces-date-range-grid-filter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CesTooltipAutoDismissDirective,
    FormsModule,
    TranslocoModule,
    TooltipModule,
    CesIconComponent,
    CesFastDateRangeInputComponent,
  ],
  templateUrl: './date-range-grid-filter.component.html',
  styleUrl: './date-range-grid-filter.component.scss',
})
export class CesDateRangeGridFilterComponent implements IFilterAngularComp {
  private readonly translate = inject(TranslocoService);
  private readonly rangeInput = viewChild(CesFastDateRangeInputComponent);
  private params!: IFilterParams;

  readonly model = signal<DateRangeTuple | null>(null);

  readonly tooltip = computed(() => {
    const range = this.model();
    // Empty filter → surface the syntax help so the compact field stays discoverable.
    if (!dateRangeIsActive(range)) return this.translate.translate('common.dateRange.syntaxHelp');
    return buildDateRangeTooltip(range, (key, params) => this.translate.translate(key, params));
  });

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  afterGuiAttached(): void {
    this.rangeInput()?.focusInput();
  }

  isFilterActive(): boolean {
    return dateRangeIsActive(this.model());
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    return dateInRange(this.params.getValue(params.node), this.model());
  }

  getModel(): DateRangeTuple | null {
    return this.model();
  }

  setModel(model: DateRangeTuple | null): void {
    this.model.set(normalizeDateRange(model));
  }

  onRangeChange(range: DateRangeTuple | null): void {
    const next = normalizeDateRange(range);
    if (dateRangeEquals(next, this.model())) return;
    this.model.set(next);
    this.params.filterChangedCallback();
  }

  onClear(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isFilterActive()) return;
    this.model.set(null);
    this.params.filterChangedCallback();
  }

  getClearAriaLabel(): string {
    return this.translate.translate('common.dateRange.clearFilter');
  }
}
